import { useEffect, useRef, useState } from "react";
import tw from "tailwind-styled-components";
import { Formik, Form } from "formik";
import { InputGroup, Label, SubmitBtn } from "../Styles/InputStyles";
import DateFilter from "./DateFilter";
import { useNavigate } from "react-router-dom";
import { useMutation } from "react-query";
import axios from "axios";
import Config from "../../Config";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import * as Yup from "yup";
import moment from "moment";
import CategoryCard from "../CategoryCard";
import MultiPropSelectModal from "../MultiPropSelectModal";

const validationSchema = Yup.object().shape({
    properties: Yup.array()
        .min(1, "At least one property is required")
        .required("Property is required"),
    reportFormat: Yup.number()
        .oneOf([1, 2], "Invalid format selected")
        .required("Report format is required"),
});

const Container = tw.div`p-6 max-w-6xl mx-auto bg-white rounded-lg shadow-sm md:mt-8 my-4 border border-gray-200`;
const Grid = tw.div`grid grid-cols-1 sm:grid-cols-2 gap-6`;
const PageWrapper = tw.div` bg-gray-50`;
const StickyFooter = tw.div`fixed bottom-0 lg:left-56 left-0 right-0 bg-white border-t border-gray-200 px-6 py-4 flex justify-end z-10`;

const ReportSpecs = ({ propertyList, reportType }) => {
    const navigate = useNavigate();
    const filteredProperties = propertyList?.filter(prop => prop.id !== 0) || [];
    const formikRef = useRef(null)
    const [showPropertyModal, setShowPropertyModal] = useState(false);
    const user = useSelector((state) => state.UserReducer.user);
    const [reportUrl, setReportUrl] = useState();
    useEffect(() => {
        if (reportUrl) {
            const url = new URL(reportUrl);
            if (url.pathname.toLowerCase().endsWith('.pdf')) {
                window.open(reportUrl);
            } else {
                window.open(
                    `https://view.officeapps.live.com/op/embed.aspx?src=${reportUrl}`
                );
            }
        }
    }, [reportUrl]);

    const downloadFunction = async (values) => {
        const allPropertiesSelected = values.properties.length === filteredProperties.length;
        const propertyIds = allPropertiesSelected ? '0' : values.properties.join(',');
        let fileType;
        if (reportType === 1) {
            fileType = values.reportFormat || 1;
        } else {
            fileType = 1;
        }
        // const downloadQuery = `startDate=${values.startDate}&endDate=${values.endDate}&s=&t=&propId=${propertyIds}&filter=&fileType=${values.reportFormat}&reportType=${reportType}`;
        const downloadQuery = `startDate=${values.startDate}&endDate=${values.endDate}&s=&t=&propId=${propertyIds}&filter=&fileType=${fileType}&reportType=${reportType}`;
        const response = await axios.get(
            `${Config.apiUrl}/report/generate?${downloadQuery}`,
            {
                headers: {
                    authorization: "Bearer " + user.token,
                    "Content-Type": "application/x-www-form-urlencoded; charset=UTF-8",
                },
            }
        );
        return response.data;
    };
    const onDownloadSuccess = (res) => {
        if (res.isSuccess === false) {
            toast.error(res.msg || "No data found for the selected date range");
        } else if (res.reportUrl === false) {
            toast.error("No data found for the selected date range");
        } else {
            setReportUrl(res.reportUrl);
            toast.success(res.msg || "Report generated successfully");
        }
    };

    const onDownloadError = (res) => {
        toast.error(res.message || "Failed to download report");
    };

    const {
        mutate: downloadReport,
        isLoading: isDownloading,
        error,
    } = useMutation(downloadFunction, {
        onSuccess: onDownloadSuccess,
        onError: onDownloadError,
    });
    const handleSelectedProperties = (selectedProperties) => {
        if (formikRef.current) {
            formikRef.current.setFieldValue("properties", selectedProperties)
        }
    }
    const handlePropertySave = (properties) => {
        handleSelectedProperties(properties)
        setShowPropertyModal(false)
    }
    const renderSelectedProperties = (selected = []) => {
        if (!selected || !selected.length) return "Select Properties"
        const selectedProperties = filteredProperties?.filter((prop) => selected.includes(prop.id))
        if (!selectedProperties?.length) return "Select Properties"
        const maxToShow = 2
        const namesToShow = selectedProperties.slice(0, maxToShow).map((prop) => prop.name)
        const remainingCount = selectedProperties.length - maxToShow
        return (
            <div className="flex items-center gap-1 truncate">
                {namesToShow.join(", ")}
                {remainingCount > 0 && <span className="ml-1 text-gray-500">+{remainingCount}</span>}
            </div>
        )
    }
    const handleExportFormatChange = (format, setFieldValue) => {
        const formatValue = format === 'excel' ? 1 : 2;
        setFieldValue("reportFormat", formatValue);
    };

    return (
        <PageWrapper>
            <Formik
                innerRef={formikRef}
                initialValues={{
                    properties: [],
                    startDate: moment().format("YYYY-MM-DD"),
                    endDate: moment().format("YYYY-MM-DD"),
                    reportFormat: 1,
                }}
                validationSchema={validationSchema}
                onSubmit={(values) => {
                    downloadReport(values);
                }}
            >
                {({ values, setFieldValue, errors, touched }) => (
                    <Form>
                        <Container>
                            <Grid>
                                <InputGroup>
                                    <Label>Select Property</Label>

                                    <CategoryCard
                                        type="button"
                                        selectedCategory={values.properties && values.properties.length > 0 ? {
                                            label: renderSelectedProperties(values.properties),
                                            value: "properties"
                                        } : null}
                                        title="Property/or Properties"
                                        onClick={() => setShowPropertyModal(true)}
                                    />
                                    {errors.properties && touched.properties && (
                                        <div className="text-red-500 text-sm mt-1">
                                            {errors.properties}
                                        </div>
                                    )}
                                </InputGroup>
                                {(reportType !== 5 && reportType !== 6 && reportType !== 11 && reportType !== 12 && reportType !== 13) &&
                                    <DateFilter setFieldValue={setFieldValue} />}
                                {errors.startDate && touched.startDate && (
                                    <div className="text-red-500 text-sm mt-1">
                                        {errors.startDate}
                                    </div>
                                )}
                                {errors.endDate && touched.endDate && (
                                    <div className="text-red-500 text-sm mt-1">
                                        {errors.endDate}
                                    </div>
                                )}

                            </Grid>

                            {/* <div className="mt-6">
                                <Label >
                                    Export Format
                                </Label>

                                <div className="flex gap-4 mt-1.5">
                                    <div className="flex bg-gray-100 px-4 py-1.5 rounded-full items-center">
                                        <label className="flex items-center gap-1.5 cursor-pointer">
                                            <input
                                                type="radio"
                                                name="exportType"
                                                value="pdf"
                                                className="text-blue-600 focus:ring-blue-500"
                                            />
                                            <span className="text-sm text-gray-800 mt-0.5">PDF</span>
                                        </label>
                                    </div>
                                    <div className="flex bg-gray-100 px-4 py-1.5 rounded-full  items-center">
                                        <label className="flex items-center gap-1.5 cursor-pointer">
                                            <input
                                                type="radio"
                                                name="exportType"
                                                value="excel"
                                                className="text-blue-600 focus:ring-blue-500"
                                            />
                                            <span className="text-sm text-gray-800 mt-0.5">Excel</span>
                                        </label>
                                    </div>
                                </div>
                            </div> */}
                            {reportType === 1 && (
                                <div className="mt-6">
                                    <Label>Export Format *</Label>

                                    <div className="flex gap-4 mt-1.5">
                                        <div
                                            className={`flex px-4 py-1.5 rounded-full items-center cursor-pointer ${values.reportFormat === 1 ? 'bg-blue-100 border border-blue-500' : 'bg-gray-100 hover:bg-gray-200'}`}
                                            onClick={() => handleExportFormatChange('excel', setFieldValue)}
                                        >
                                            <label className="flex items-center gap-1.5 cursor-pointer">
                                                <input
                                                    type="radio"
                                                    name="exportType"
                                                    value="excel"
                                                    checked={values.reportFormat === 1}
                                                    onChange={() => handleExportFormatChange('excel', setFieldValue)}
                                                    className="text-primary-color focus:ring-primary-color"
                                                />
                                                <span className={`text-sm ${values.reportFormat === 1 ? 'text-primary-color font-medium' : 'text-gray-800'}`}>
                                                    Excel
                                                </span>
                                            </label>
                                        </div>
                                        <div
                                            className={`flex px-4 py-1.5 rounded-full items-center cursor-pointer ${values.reportFormat === 2 ? 'bg-blue-100 border border-blue-500' : 'bg-gray-100 hover:bg-gray-200'}`}
                                            onClick={() => handleExportFormatChange('pdf', setFieldValue)}
                                        >
                                            <label className="flex items-center gap-1.5 cursor-pointer">
                                                <input
                                                    type="radio"
                                                    name="exportType"
                                                    value="pdf"
                                                    checked={values.reportFormat === 2}
                                                    onChange={() => handleExportFormatChange('pdf', setFieldValue)}
                                                    className="text-primary-color focus:ring-primary-color"
                                                />
                                                <span className={`text-sm ${values.reportFormat === 2 ? 'text-primary-color font-medium' : 'text-gray-800'}`}>
                                                    PDF
                                                </span>
                                            </label>
                                        </div>


                                    </div>


                                    {errors.reportFormat && touched.reportFormat && (
                                        <div className="text-red-500 text-sm mt-1">
                                            {errors.reportFormat}
                                        </div>
                                    )}
                                </div>)}
                        </Container>


                        <StickyFooter>
                            <button
                                type="button"
                                className="h-12 px-4 mr-4 text-gray-600 hover:text-primary-color md:text-base "
                                onClick={() => navigate(-1)}
                            >
                                Back
                            </button>
                            <SubmitBtn type="submit" disabled={isDownloading}>
                                {isDownloading ? "Downloading..." : "Generate Report"}
                            </SubmitBtn>
                        </StickyFooter>
                        <MultiPropSelectModal
                            isOpen={showPropertyModal}
                            onClose={() => setShowPropertyModal(false)}
                            onSelect={handlePropertySave}
                            options={filteredProperties}
                            selectedProperties={values.properties || []}
                            width="35%"
                        />
                    </Form>
                )}
            </Formik>
        </PageWrapper>
    );
};

export default ReportSpecs;